import { useEffect, useRef } from 'react';
import { recordEvent } from '@department-of-veterans-affairs/platform-monitoring/exports';

/**
 * Emits the GA4 `nav-alert-box-load` event once per distinct alert while the
 * alert is visible. Re-fires when the alert identity (alertId/headline) changes
 * so a single mounted component that swaps between alerts tracks each one, but
 * never re-fires on an unrelated re-render. Use this alone in apps that already
 * handle Datadog alert telemetry separately (e.g. VAOS's useAlertTelemetry);
 * use useAlertTracking when a single call should report to both GA4 and RUM.
 *
 * @param {Object}  options
 * @param {string}  options.headline   — alert headline text (GA4 `alert-box-headline`)
 * @param {string}  options.status     — 'error' | 'warning' | 'info' | 'success'
 * @param {string}  [options.alertId]  — stable id sent as the GA4 param `alert-box-id`
 * @param {boolean} [options.isVisible=true] — whether the alert is currently rendered
 */
const useAlertGa4Tracking = ({
  headline,
  status,
  alertId,
  isVisible = true,
}) => {
  const trackedKey = useRef(null);

  useEffect(() => {
    const key = `${alertId ?? ''}::${headline ?? ''}`;
    if (isVisible && headline && trackedKey.current !== key) {
      recordEvent({
        event: 'nav-alert-box-load',
        action: 'load',
        'alert-box-headline': headline,
        'alert-box-status': status,
        ...(alertId ? { 'alert-box-id': alertId } : {}),
      });
      trackedKey.current = key;
    }
  }, [isVisible, headline, status, alertId]);
};

export default useAlertGa4Tracking;
